/**
 * The `#558` contact-linking wire error codes and the **German** sentence each one becomes on screen.
 *
 * Written to the same two rules as `second-factor-errors.ts`, for the same reasons:
 *
 * 1. **Never render the backend's `description`.** It is English, written for an operator reading a log, and
 *    this app has no i18n layer. `ErrorCatalog` may reword a description at any time — it is documentation.
 * 2. **One mapping, several surfaces.** The lockout wizard, the voluntary wizard on „Meine Verknüpfungen" and
 *    the unlink confirmation can all receive the same code, and a per-surface copy is a copy that drifts.
 *
 * Addressing is the formal *Sie*.
 */

/**
 * The codes the monolith actually registers for #558, confirmed against the backend.
 *
 * Spelled out as literals rather than derived: these strings are the contract, and a typo here is a branch
 * that silently never fires. The mapping stays a lookup with a fallback rather than an exhaustive switch, so
 * a code added later costs the reader nothing worse than {@link CONTACT_LINK_FALLBACK_MESSAGE}.
 *
 * **Three outcomes are deliberately absent, and adding them would be a bug**: `expired`, `declined` and
 * `awaitingShareConsent` arrive as a challenge **state** from `GET …/challenge/{id}`, never as an error. They
 * are ordinary progress through the flow — the wizard gives each its own wording and its own next action —
 * and mapping them here would put a red error card over a decision the employee just made on purpose.
 */
export const ContactLinkErrorCodes = {
  /** The channel exists but cannot be started right now. */
  ChannelUnavailable: 'E_CONTACT_CHANNEL_UNAVAILABLE',
  /** 409 — no messenger bot is configured for this installation. */
  BotNotConfigured: 'E_CONTACT_BOT_NOT_CONFIGURED',
  /** This account already has a live link for the channel — there is nothing to link. */
  AlreadyLinked: 'E_CONTACT_ALREADY_LINKED',
  /** The link named for unlinking does not exist. */
  LinkNotFound: 'E_CONTACT_LINK_NOT_FOUND',
  /** 404 — the challenge is unknown, already spent, **or belongs to another account** (one code for all). */
  ChallengeInvalid: 'E_CONTACT_CHALLENGE_INVALID',
  /** 403 — nothing to lift: a reissue asked for by a session carrying no contact restriction. */
  ReissueNotApplicable: 'E_CONTACT_REISSUE_NOT_APPLICABLE',
  /** 403 — a contact endpoint was called by a session that is not an employee's. */
  NotAnEmployeeSession: 'E_CONTACT_NOT_AN_EMPLOYEE_SESSION',
  /** 400 — the requested channel is not one this server knows. */
  ChannelUnknown: 'E_CONTACT_CHANNEL_UNKNOWN',
  /** 400 — the requested purpose is neither `link` nor `reverify`. */
  PurposeUnknown: 'E_CONTACT_PURPOSE_UNKNOWN',
  /** 409 — a re-verification was asked for a channel this account has no link on. */
  ReverifyWithoutLink: 'E_CONTACT_REVERIFY_WITHOUT_LINK',
  /** 403 — the contact gate refused an ordinary route for a restricted session (the middleware's own code). */
  GateRestricted: 'E_CONTACT_GATE_RESTRICTED'
} as const;

export type ContactLinkErrorCode = typeof ContactLinkErrorCodes[keyof typeof ContactLinkErrorCodes];

/** The German fallback — every unmapped code, every network failure, every response with no code at all. */
export const CONTACT_LINK_FALLBACK_MESSAGE =
  'Die Aktion konnte nicht ausgeführt werden. Bitte versuchen Sie es erneut.';

/**
 * The sentence each code becomes. Every one names the **next action**, because that is the only thing the
 * reader can do with it — and two of them deliberately name a *person* instead, since „erneut versuchen" is
 * exactly what cannot help when a bot is unconfigured.
 */
const MESSAGES: Record<string, string> = {
  [ContactLinkErrorCodes.ChannelUnavailable]:
    'Dieser Kanal steht derzeit nicht zur Verfügung. Bitte wenden Sie sich an Ihre Vorgesetzten — die '
    + 'Verknüpfung kann erst eingerichtet werden, wenn der Kanal freigeschaltet ist.',
  // Names the installation rather than "Ihr Unternehmen": the bot is a server-side setting, and pointing at
  // the wrong person is what makes an unfixable screen feel like the reader's fault.
  [ContactLinkErrorCodes.BotNotConfigured]:
    'Der Messenger-Bot ist auf dieser Installation nicht eingerichtet. Die Verknüpfung kann erst hergestellt '
    + 'werden, wenn Ihre Administration den Bot hinterlegt hat.',
  [ContactLinkErrorCodes.AlreadyLinked]:
    'Dieser Kanal ist bereits mit Ihrem Konto verknüpft. Bitte laden Sie die Seite neu.',
  [ContactLinkErrorCodes.LinkNotFound]:
    'Diese Verknüpfung wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  // ONE sentence for unknown, spent and not-yours alike, because the server answers all three with this code —
  // and because "dieser Code gehört zu einem anderen Konto" would tell a stranger which codes exist.
  [ContactLinkErrorCodes.ChallengeInvalid]:
    'Dieser Verknüpfungscode ist nicht mehr gültig. Bitte fordern Sie einen neuen Code an.',
  [ContactLinkErrorCodes.ReissueNotApplicable]:
    'Für diese Sitzung gibt es nichts freizuschalten. Bitte laden Sie die Seite neu.',
  [ContactLinkErrorCodes.NotAnEmployeeSession]:
    'Verknüpfungen von Kontaktkanälen stehen nur Mitarbeiterkonten zur Verfügung.',
  // The last two are configuration/programming faults rather than anything the reader did, so they say what
  // is true — "nicht verfügbar" — instead of implying a mistyped entry the reader never made.
  [ContactLinkErrorCodes.ChannelUnknown]:
    'Dieser Kanal wird von dieser Installation nicht unterstützt. Bitte wenden Sie sich an Ihre Vorgesetzten.',
  [ContactLinkErrorCodes.PurposeUnknown]:
    'Diese Aktion wird nicht unterstützt. Bitte laden Sie die Seite neu.',
  [ContactLinkErrorCodes.ReverifyWithoutLink]:
    'Für diesen Kanal besteht keine Verknüpfung, die bestätigt werden könnte. Bitte richten Sie die '
    + 'Verknüpfung zuerst ein.',
  [ContactLinkErrorCodes.GateRestricted]:
    'Bitte richten Sie zuerst die erforderliche Verknüpfung ein — danach steht Ihnen wieder alles offen.'
};

/**
 * The `code` of a structured `ApiError` response, or null when the failure carries none (a network error, a
 * proxy's HTML error page, an older server).
 *
 * Written defensively because it is fed straight from an rxjs `error` callback, whose argument is `unknown`.
 */
export function readContactErrorCode(err: unknown): string | null {
  const code = (err as {error?: {code?: unknown}} | null)?.error?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * True when the failure says the CHANNEL itself cannot be used right now, rather than that this attempt went
 * wrong.
 *
 * The distinction decides whether the screen offers „Erneut versuchen" at all: for these codes a retry is a
 * button whose only possible outcome is the same message again, and a required-but-unavailable channel is
 * precisely the shape that turns a lockout screen into a dead end. The screen says who can fix it instead.
 *
 * `E_CONTACT_CHANNEL_UNKNOWN` belongs here for the same reason and not for the same cause: it is a
 * configuration mismatch (the SPA offered a channel this server does not implement), which no amount of
 * pressing a button resolves either.
 */
export function isChannelUnavailable(err: unknown): boolean {
  const code = readContactErrorCode(err);
  return code === ContactLinkErrorCodes.ChannelUnavailable
    || code === ContactLinkErrorCodes.BotNotConfigured
    || code === ContactLinkErrorCodes.ChannelUnknown;
}

/**
 * The German sentence for a failed contact-linking call.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the sentence for an unrecognised failure. Override it where the surface can say something
 *                 more specific about what was being attempted.
 */
export function describeContactLinkError(err: unknown,
                                         fallback: string = CONTACT_LINK_FALLBACK_MESSAGE): string {
  const code = readContactErrorCode(err);
  return (code && MESSAGES[code]) || fallback;
}
